import { AppRoute } from './AppRoute';

export class AppRouteGroup<
  TGroupPath extends string,
  TGroupParentPathWithSlash extends string = '',
  TGroupParam extends string = never
> {
  public readonly path: TGroupPath;

  public readonly parentPathWithSlash: TGroupParentPathWithSlash;

  public readonly params: TGroupParam[];

  constructor(data: {
    path: TGroupPath;
    parentPathWithSlash?: TGroupParentPathWithSlash;
    params?: TGroupParam[];
  }) {
    this.path = data.path;
    // @ts-expect-error fallback to an empty string
    this.parentPathWithSlash = data.parentPathWithSlash || '';
    this.params = data.params || [];
  }

  /**
   * Create a route that uses this group's path & params
   */
  route<
    TRelativePath extends string,
    TRelativeParam extends string = never,
    TSearchParam extends string = never
  >(data: {
    path: TRelativePath;
    params?: TRelativeParam[];
    searchParams?: TSearchParam[];
  }): AppRoute<
    `${TGroupParentPathWithSlash}${TGroupPath}`,
    TGroupParam,
    TRelativePath,
    TRelativeParam,
    TSearchParam
  > {
    return new AppRoute({
      groupPath: this.fullPath,
      groupParams: this.params,
      relativePath: data.path,
      relativeParams: data.params,
      searchParams: data.searchParams,
    });
  }

  get fullPath(): `${TGroupParentPathWithSlash}${TGroupPath}` {
    return `${this.parentPathWithSlash}${this.path}`;
  }

  get wrapperPath(): `${TGroupPath}/*` {
    return `${this.path}/*`;
  }

  get fullWrapperPath(): `${TGroupParentPathWithSlash}${TGroupPath}/*` {
    return `${this.fullPath}/*`;
  }

  subgroup<
    TSubgroupPath extends string,
    TSubgroupParam extends string = never
  >(data: {
    path: TSubgroupPath;
    params?: TSubgroupParam[];
  }): AppRouteGroup<
    TSubgroupPath,
    `${TGroupPath}/`,
    TGroupParam | TSubgroupParam
  > {
    return new AppRouteGroup({
      path: data.path,
      parentPathWithSlash: `${this.path}/`,
      params: [...this.params, ...(data.params || [])],
    });
  }
}
